// src/app/shared/services/edit/package-edit.service.ts
import { Injectable, inject } from '@angular/core'

import {
  DeepPartial,
  Hunt,
  GeneratedContent,
  deepMerge,
} from '@cyborg-monorepo/hunter/common'

import { NavigationService } from '../navigation.service'
import { SessionService } from '../session.service'
import { UndoRedoService } from '../undo-redo.service'

@Injectable({ providedIn: 'root' })
export class PackageEditorService {
  private readonly session = inject(SessionService)
  private readonly nav = inject(NavigationService)
  private readonly undoRedo = inject(UndoRedoService<Hunt>)

  /**
   * Apply a deep partial update to the currently selected hunt package.
   * Snapshots the current state for undo before applying.
   */
  async patchPackage(
    update: DeepPartial<Hunt['huntPackages'][number]>,
  ): Promise<void> {
    const hunt = this.session.activeHunt()
    const pkgId = this.nav.packageId()
    if (!hunt || !pkgId) return

    // Snapshot for undo
    this.undoRedo.push(hunt)

    // Deep merge patch into the selected package
    const updatedHunt: Hunt = {
      ...hunt,
      huntPackages: hunt.huntPackages.map((pkg) =>
        pkg.UUID === pkgId ? deepMerge(pkg, update) : pkg,
      ),
    }

    // Persist and re-emit new state
    this.session.activeHunt.set(updatedHunt)
  }

  /**
   * Append generated pivot queries into the corresponding node of
   * `originalAnalysis` based on the current navigation context.
   */
  async appendPivotsToOriginalAnalysis(
    newPivots: GeneratedContent[],
  ): Promise<void> {
    const hunt = this.session.activeHunt()
    const pkgId = this.nav.packageId()
    const envId = this.nav.environmentId()
    const toolId = this.nav.toolId()
    const queryId = this.nav.selectedQueryId()
    const pivotPath = this.nav.pivotPath()

    if (!hunt || !pkgId || !envId || !toolId || !queryId) return

    this.undoRedo.push(hunt)

    const updated: Hunt = structuredClone(hunt)
    const pkg = updated.huntPackages.find((p) => p.UUID === pkgId)
    if (!pkg) return
    const env = pkg.originalAnalysis?.find((e) => e.id === envId)
    if (!env) return
    const tool = env.tools.find((t) => t.id === toolId)
    if (!tool?.generatedContent?.Hunt) return

    let node = tool.generatedContent.Hunt.find((q) => q.id === queryId)
    if (!node) return

    for (const id of pivotPath) {
      const next = node.pivots?.find((p) => p.id === id)
      if (!next) return
      node = next
    }

    node.pivots = [...(node.pivots ?? []), ...newPivots]

    this.session.activeHunt.set(updated)
  }
}
